import { Injectable } from '@angular/core';
import { EmailService } from './email.service';
import { forkJoin, BehaviorSubject, Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.state';
import { map, mergeMap } from 'rxjs/operators';
import { LeadService } from '../../client-services/leads.service';
import { environment } from 'src/environments/environment';


/**
 * @description This class handles all functional related to email feeds contextualization
 */
interface ContextData {
    context_name: string;
    context_entity_id: string; // ID of the item in view
    context_primary_email?: string;
    context_email_list: Array<string>;
}

@Injectable({
    providedIn: 'root',
})
export class EmailFeedService {

    orgId: number;
    userId: number;

    default_messages = new BehaviorSubject<any>([]);
    messages = new BehaviorSubject<any>([]);

    threads: Array<any>;

    public selectedMail = new BehaviorSubject<any>({});
    public mailAttachments = new BehaviorSubject<any[]>([]);
    public mailClients = new BehaviorSubject<any[]>([]);

    constructor(
        private emailService: EmailService,
        private leadService: LeadService,
        private store: Store<AppState>,
    ) {
        this.store.select('userInfo').subscribe(info => {
            if (Object.keys(info).length) {
                this.orgId = info.organization.id
                this.userId = info.user.id;
            }
        })
    }

    check_if_thread_has_lead(index = 0) {
        if (this.default_messages.getValue().length <= index) return;
        const message = this.messages[index];
        // Ensure `message` is defined before accessing properties
        if (!message) {
            console.warn(`Message at index ${index} is undefined.`);
            return;
        }
const { recipient = '', cc_recipients = [], bcc_recipients = [] } = message || {};

        // Ensure `recipient` and other fields exist
        if (!recipient && (!cc_recipients || !bcc_recipients)) {
            console.warn(`Message at index ${index} has missing recipient fields:`, message);
            return;
        }
        const query_string = `email=${[recipient, ...cc_recipients, ...bcc_recipients].join()}`;
        this.leadService.filterLead(query_string).subscribe((response: any) => {
            if (response && response.success) {
                if (this.messages[index]) {
                    this.messages[index].has_lead = !!response.payload.length;
                }
                this.check_if_thread_has_lead(index + 1);
            }
        });
    }
    

    fetch_emails(user_email, general_query, user_query, deal_query, page = 0, population = 10) {
        // fetch thread requests
        this.default_messages.next([]);
        const fetch_threads_by_emails = this.emailService.filterThreads({
            org_id: this.orgId,
            emails: `${general_query}${user_email ? ',' + user_email : ''}`
            .replace(/,$/, '')
            .replace(/,,+/g, ','),    
            page,
            population,
        });

        const fetch_threads_by_user = this.emailService.filterThreads({
            org_id: this.orgId,
            user_id: `${user_query || '-8989'}`,
            page,
            population,
        });

        const fetch_threads_by_deal_id = this.emailService.filterThreads({
            org_id: this.orgId,
            'deal.id': `${deal_query || 'not a deal'}`,
            page,
            population,
        });

        return forkJoin([fetch_threads_by_emails, fetch_threads_by_user, fetch_threads_by_deal_id])
            .pipe(map((responses: any) => {
                let threads = [], thread_ids = [];

                responses.forEach(response => {
                    if (response.success) {
                        threads = [...threads, ...response.payload]
                    }
                });
                this.threads = this.transformThreadArrayIntoObject(threads);
                thread_ids = threads.map(thread => thread.thread_id);
                return thread_ids;
            }))
            .pipe(mergeMap((thread_ids: any) => {
                if (thread_ids.length < 1) {
                    return this.emailService.filterMessages({ thread_id: ['nothreadforyou'] });
                } else {
                    return this.emailService.filterMessages({ thread_id: thread_ids });
                }
            }))
            .pipe(map((response: any) => {
                const { success, payload } = response;
                if (!success) return [];
                const messages = this.sortMessagesByThread(payload);
                this.setMessages(messages);
                return messages;
            }));
    }

    handleNewMessage(message) {
        const messages = [...this.default_messages.getValue(), { ...message }];
        const thread_messages = this.sortMessagesByThread(messages);
        thread_messages.sort((a, b) => Number(b.time_stamp) - Number(a.time_stamp));

        //
        this.messages.next(thread_messages);
        this.default_messages.next(thread_messages);
    }

    /**
     *
     * @param context_data set based on the feed context
     *
     */
    process_context_data(context_data: any): ContextData {
        const { name, data } = context_data;
        let return_data: ContextData = {
            context_name: name.toLowerCase(),
            context_entity_id: data.id,
            context_primary_email: '',
            context_email_list: []
        };

        switch (name.toLowerCase()) {
            case 'lead':
                return_data.context_primary_email = data.email.toLowerCase() || '';
            case 'salesperson':
            case 'contact':
                return_data.context_primary_email = data.email.toLowerCase() || '';
                break;
            case 'team':
                return_data.context_email_list = data.email_list.map(email => email.toLowerCase()) || [];
                break;
            case 'company':
            case 'deal':
                return_data.context_primary_email = data.email ? data.email.toLowerCase() : '';
                return_data.context_email_list = data.email_list.map(email => email.toLowerCase()) || [];
                break;
            case 'dashboard':
                return_data.context_primary_email = data.email.toLowerCase() || '';
                break;
            default:
        }

        return return_data;
    }

    /**
    *
    * @param service the connected email service e.g 'gmail'
    * @param message the message from the database
    */
    processMailBody(service: string, message: any) {
        let { attachments, hasAttachment } = message;
        let processedBody: any = {};
        switch (service) {
            case 'office':
                processedBody = {
                    content: message.body.content,
                    subject: message.subject,
                    sender: message.sender,
                    recipient: message.metadata.to[0].address,
                    cc_recipients: message.metadata.cc.map(recipient => recipient.address),
                    bcc_recipients: message.metadata.bcc.map(recipient => recipient.address)
                }
                break;
            case 'gmail':
                let body = '';
                //case 1 
                // has no body 
                if (message.body) {

                    body = message.body.html || '';
                    if (!body && message.body.body) {
                        body = message.body.body.html || message.body.body.text || message.body.body || '';
                    }
                }

                processedBody = {
                    content: body,
                    subject: message.body.subject,
                    sender: message.body.from == message.sender ? message.body.to : message.body.from,
                    recipient: message.body.to,
                    cc_recipients: message.body.cc,
                    bcc_recipients: message.body.bcc,
                }

                break;
            // case 'imap':
            //     let mail_body;
            //     if (typeof message.body === 'string') {
            //         mail_body = message.body;
            //     } else {
            //         mail_body = typeof message.body.text === "string" ? message.body.text : message.body.text.text;
            //     }

            //     processedBody = {
            //         content: mail_body,
            //         subject: message.subject || message.headers.subject,
            //         sender: message.sender,
            //     }
            default:
        }

        let bodyText = processedBody.content;

        return {
            ...processedBody,
            attachments,
            hasAttachment: hasAttachment || attachments.length,
            content: bodyText,
            snippet: bodyText, //? bodyText.substr(0, 25) : '',
        };
    }

    setMessages(messages) {
        let message_map = [...this.default_messages.getValue(), ...messages].reduce((m_map, e) => ({
            ...m_map,
            [e.index]: e,
        }), {});

        this.default_messages.next(Object.values(message_map));

        this.check_if_thread_has_lead(this.messages.getValue().length);
    }

    sortMessagesByThread(messages) {
        let threadMap = {};
        let threadMessage = [];

        messages.forEach(message => {
            const { attachments, thread_id } = message;
            if (!threadMap[thread_id]) {
                threadMap[thread_id] = {
                    message: {},
                    attachments: [],
                }
            }
            if (attachments) {
                threadMap[thread_id].attachments = [
                    ...threadMap[thread_id].attachments,
                    ...attachments,
                ];
            }
            threadMap[thread_id].message = { ...message };
        });

        Object.keys(threadMap).forEach((thread_id, index) => {
            let { attachments, message } = threadMap[thread_id];
            let thread = {
                ...this.threads[thread_id],
                ...threadMap[thread_id]
            };

            const processed_message = this.processMailBody(message.service, message);
            message = {
                ...message,
                ...processed_message,
                index,
                date: message.time_stamp,
                hasAttachment: !!attachments.length,
                attachments,
                thread,
                itemType: 'email',
            }


            threadMessage = [
                message,
                ...threadMessage
            ];
        });
        return threadMessage;
    }

    transformThreadArrayIntoObject(data: Array<any>): any {
        let threads: any = {};
        data.forEach(datum => {
            threads = {
                ...threads,
                [datum.thread_id]: { ...datum }
            }
        });

        return threads;
    }

    updateMessageReadStatus(message, index): any {
        this.emailService.updateMessage({ ...message, status: 'read' }).subscribe((response: any) => {
            if (response.success) {
                const default_messages = this.default_messages.getValue();
                default_messages[index].status = 'read';
                this.default_messages.next(default_messages);
            }
        });
    }
}